import React, {useLayoutEffect, useRef, useState} from 'react';
import {useCarouselNavigation} from '../../utils';

interface IMobileCarousel<T> {
  slides: readonly T[];
  children?: React.ReactNode;
  autoplayIntervalMs?: number;
  dotActiveClassName?: string;
  dotInactiveClassName?: string;
}

export const MobileCarousel = <T,>({
  slides,
  children,
  autoplayIntervalMs = 2900,
  dotActiveClassName = 'bg-primaryred bg-opacity-100',
  dotInactiveClassName = 'bg-primaryred bg-opacity-40',
}: IMobileCarousel<T>) => {
  const {currentSlideIndex, setCurrentSlideIndex, swipeHandlers} =
    useCarouselNavigation({
      itemCount: slides.length,
      wrap: true,
      autoplayIntervalMs,
    });

  // Track width must match this carousel's own rendered container, not the
  // viewport — some carousels are full-bleed, others sit inside page
  // margins, so `window.innerWidth` overshoots for the latter.
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }
    setContainerWidth(element.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={`lg:hidden relative w-full`} {...swipeHandlers}>
      <div className="overflow-hidden" ref={containerRef}>
        <div
          className={`flex transition ease-out duration-200`}
          style={{
            width: containerWidth,
            transform: `translatex(-${currentSlideIndex * 100}%)`,
          }}>
          {children}
        </div>
        <div className="pt-8 flex justify-center gap-2 w-full">
          {slides.map((s, i) => (
            <button
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => {
                setCurrentSlideIndex(i);
              }}
              className={`w-10 h-1 ${
                i === currentSlideIndex
                  ? dotActiveClassName
                  : dotInactiveClassName
              }`}
              key={i}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
